import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/features/auth';
import { queryKeys } from '@/lib/queryKeys';
import { openRealtime, type RealtimeConnection } from '@/services/realtime';
import { notificationContent } from '../lib/notificationContent';
import type { AppNotification, NotificationReadEvent } from '../types';
import { applyRead } from './useNotifications';

/** Wait before rejoining after the server closed the socket. */
const REJOIN_DELAY_MS = 5000;

function isNotification(value: unknown): value is AppNotification {
	const candidate = value as Partial<AppNotification> | null;
	return (
		typeof candidate?.id === 'string' &&
		typeof candidate.type === 'string' &&
		typeof candidate.title === 'string'
	);
}

function isReadEvent(value: unknown): value is NotificationReadEvent {
	const candidate = value as Partial<NotificationReadEvent> | null;
	return (
		typeof candidate?.id === 'string' && typeof candidate.readAt === 'string'
	);
}

/**
 * Keeps the notification caches live over the `/realtime` socket and shows a
 * toast for new notifications. One connection per access token: a refreshed
 * token opens a new connection.
 */
export function useNotificationRealtime(options: {
	enabled: boolean;
	/** No toast while the user is already looking at the list. */
	isListOpen: () => boolean;
}): void {
	const { enabled } = options;
	const accessToken = useAuthStore((state) => state.accessToken);
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	// Latest callbacks without reconnecting when they change.
	const latest = useRef({ isListOpen: options.isListOpen, navigate });
	latest.current = { isListOpen: options.isListOpen, navigate };

	useEffect(() => {
		if (!enabled || !accessToken) return;

		let connection: RealtimeConnection | null = null;
		let rejoinTimer: number | null = null;
		let closed = false;

		const onCreated = (payload: unknown) => {
			if (!isNotification(payload)) return;
			void queryClient.invalidateQueries({
				queryKey: [...queryKeys.notifications.all, 'list'],
			});
			void queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.unreadCount(),
			});
			if (latest.current.isListOpen()) return;

			const content = notificationContent(payload);
			const link = content.link;
			toast(content.title, {
				id: payload.id,
				description: content.description ?? undefined,
				action: link
					? {
							label: content.actionLabel ?? 'View',
							onClick: () => latest.current.navigate(link),
						}
					: undefined,
			});
		};

		const onRead = (payload: unknown) => {
			if (isReadEvent(payload))
				applyRead(queryClient, payload.id, payload.readAt);
		};

		// The server closes the socket when the token expires. Refetching the
		// badge goes through the HTTP client, which refreshes an expired token;
		// a new token re-runs this effect. Otherwise rejoin after a pause.
		const onServerDisconnect = () => {
			void queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.unreadCount(),
			});
			rejoinTimer = window.setTimeout(() => {
				rejoinTimer = null;
				if (!closed) connection?.reconnect();
			}, REJOIN_DELAY_MS);
		};

		void openRealtime(accessToken)
			.then((opened) => {
				if (closed) {
					opened.close();
					return;
				}
				connection = opened;
				opened.on('notification.created', onCreated);
				opened.on('notification.read', onRead);
				opened.onServerDisconnect(onServerDisconnect);
			})
			.catch(() => {
				// Realtime is an enhancement; the badge still refreshes by polling.
			});

		return () => {
			closed = true;
			if (rejoinTimer !== null) window.clearTimeout(rejoinTimer);
			connection?.close();
		};
	}, [enabled, accessToken, queryClient]);
}
